import type { TextProps } from 'uikit';
import { Text } from 'uikit';

import { Tooltip } from '@consta/uikit/Tooltip';
import type React from 'react';
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface ITextWithTooltip extends TextProps {
    tooltipText: string;
    text: string;
    tooltipColor?: string;
    className?: string;
    showMode?: 'default' | 'overflow';
    enterDelay?: number;
    exitDelay?: number;
}

const spanStyles: CSSProperties = {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
};

const tooltipStyles: CSSProperties = {
    wordBreak: 'break-word',
};

export const TextWithTooltip: React.FC<ITextWithTooltip> = ({
    tooltipText,
    text,
    tooltipColor = undefined,
    showMode,
    enterDelay = 200,
    exitDelay = 200,
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isOverflow, setIsOverflow] = useState(false);

    const ref = useRef<HTMLSpanElement | null>(null);

    const onMouseEnter = useCallback(() => {
        setTimeout(() => setIsVisible(showMode === 'overflow' ? isOverflow : true), enterDelay);
    }, [isOverflow, showMode, enterDelay]);

    const onMouseLeave = useCallback(() => {
        setTimeout(() => setIsVisible(false), exitDelay);
    }, [exitDelay]);

    useEffect(() => {
        if (!ref.current) {
            return;
        }

        setIsOverflow(ref.current.scrollWidth > ref.current.clientWidth);
    }, [ref.current?.offsetWidth, ref.current?.clientWidth]);

    return (
        <>
            <span style={spanStyles} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} ref={ref}>
                {text}
            </span>

            {isVisible && (
                <Tooltip color={tooltipColor} anchorRef={ref}>
                    <Text style={{ ...tooltipStyles, color: tooltipColor }}>{tooltipText}</Text>
                </Tooltip>
            )}
        </>
    );
};

TextWithTooltip.displayName = 'TextWithTooltip';
